import { css } from "@emotion/css";
import { ArrowUpRightFromSquareIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens, space, text } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
import { EmptyStateDefaultIllustration } from "../../Illustrations/EmptyStateDefaultIllustration";
import { NoEntitiesIllustration } from "../../Illustrations/NoEntitiesIllustration";
import { ExternalLink } from "../../Link/ExternalLink";
import type { PrimaryPageAction, PageAction } from "../../PageActions/PageActions";
import { toInternalPageAction, toInternalPrimaryPageAction, PageActionComponent } from "../../PageActions/PageActions";
type ExcludePageActionType<T extends {
    type: unknown;
}, TAction = PageAction> = TAction extends T ? Exclude<TAction, T> : TAction;
export type InlineEmptyStateOnboardingPrimaryPageAction = ExcludePageActionType<{
    type: "custom";
}, PrimaryPageAction>;
export type InlineEmptyStateOnboardingSecondaryPageAction = ExcludePageActionType<{
    type: "custom";
}, PageAction> & {
    buttonType: "secondary";
};
export interface InlineEmptyStateExternalOnboardingPageAction {
    label: string;
    accessibleName?: string;
    disabled?: boolean | Promise<unknown>;
    onClick: (event: React.MouseEvent | undefined) => Promise<unknown> | void;
}
export type LearnMoreActionType = {
    label: string;
    onClick?: () => void;
    path: string;
};
type InlineOnboardingIllustrationType = "NoEntities" | "EmptyState" | "Custom";
type BaseProps = {
    illustration: InlineOnboardingIllustrationType;
    learnMore?: LearnMoreActionType;
    actions?: ActionProps;
};
type PredefinedIllustrationProps = BaseProps & {
    illustration: "NoEntities" | "EmptyState";
};
type CustomIllustrationProps = BaseProps & {
    illustration: "Custom";
    image: React.ReactElement;
};
type IllustrationProps = PredefinedIllustrationProps | CustomIllustrationProps;
type CustomTitleAndDescriptionProps = {
    title: string;
    description: string;
};
type StandardTitleAndDescriptionProps = {
    entityName: string;
};
type TitleAndDescriptionProps = CustomTitleAndDescriptionProps | StandardTitleAndDescriptionProps;
type PrimaryAndSecondaryActionProps = {
    primary: InlineEmptyStateOnboardingPrimaryPageAction;
    secondary?: InlineEmptyStateOnboardingSecondaryPageAction;
};
type ExternalActionProps = {
    external: InlineEmptyStateExternalOnboardingPageAction;
};
type ActionProps = PrimaryAndSecondaryActionProps | ExternalActionProps;
export type InlineEmptyStateOnboardingProps = IllustrationProps & TitleAndDescriptionProps;
export function InlineEmptyStateOnboarding(props: InlineEmptyStateOnboardingProps): JSX.Element {
    const { learnMore, actions } = props;
    const [title, description] = getTitleAndDescription(props);
    return (<div className={inlineEmptyStateOnboardingStyles.root}>
            <InlineEmptyStateImage {...props}/>
            <h2 className={inlineEmptyStateOnboardingStyles.heading}>{title}</h2>
            {description !== null && <p className={inlineEmptyStateOnboardingStyles.description}>{description}</p>}
            {actions && (<InlineEmptyStateOnboardingPageActions primaryAction={"primary" in actions ? actions.primary : undefined} externalLinkAction={"external" in actions ? actions.external : undefined} secondaryAction={"secondary" in actions ? actions.secondary : undefined} learnMore={learnMore}/>)}
        </div>);
}
function getTitleAndDescription(props: InlineEmptyStateOnboardingProps): [
    string,
    string | null
] {
    const getTitleFromEntityName = (entityName: string) => `You don't have any ${entityName} yet.`;
    // If we have been provided with CustomTitleAndDescriptionProps values, preference using them
    // It is enough to check title is in the props
    if ("title" in props) {
        return [props.title, props.description];
    }
    else if (props.actions) {
        const actionLabel = getActionLabel(props.actions);
        return [getTitleFromEntityName(props.entityName), `Start creating by clicking on ${actionLabel}`];
    }
    else {
        return [getTitleFromEntityName(props.entityName), null];
    }
}
function getActionLabel(props: ActionProps): string {
    return "primary" in props ? props.primary.label : props.external.label;
}
function InlineEmptyStateImage(props: IllustrationProps) {
    return <div className={inlineEmptyStateImageStyles}>{getIllustration(props)}</div>;
}
function getIllustration(props: IllustrationProps): React.ReactElement {
    switch (props.illustration) {
        case "EmptyState":
            return <EmptyStateDefaultIllustration />;
        case "NoEntities":
            return <NoEntitiesIllustration />;
        case "Custom":
            return props.image;
        default:
            exhaustiveCheck(props, "Not all illustration types have been handled");
    }
}
const inlineEmptyStateImageStyles = css({
    maxWidth: "120px",
    maxHeight: "120px",
});
interface InlineEmptyStateOnboardingPageActionsProps {
    primaryAction?: InlineEmptyStateOnboardingPrimaryPageAction;
    externalLinkAction?: InlineEmptyStateExternalOnboardingPageAction;
    secondaryAction?: InlineEmptyStateOnboardingSecondaryPageAction;
    learnMore?: LearnMoreActionType;
}
function InlineEmptyStateOnboardingPageActions({ primaryAction, externalLinkAction, secondaryAction, learnMore }: InlineEmptyStateOnboardingPageActionsProps): JSX.Element {
    return (<div className={inlineEmptyStateOnboardingStyles.actions}>
            {primaryAction && <OnboardingPrimaryPageAction primaryAction={primaryAction}/>}
            {externalLinkAction && <OnboardingExternalPageAction externalAction={externalLinkAction}/>}
            {secondaryAction && <OnboardingPageAction action={secondaryAction}/>}
            {learnMore && <ExternalLink href={learnMore.path} label={learnMore.label} onClick={learnMore.onClick}/>}
        </div>);
}
function OnboardingPrimaryPageAction({ primaryAction }: {
    primaryAction: InlineEmptyStateOnboardingPrimaryPageAction;
}) {
    const internalPrimaryPageAction = toInternalPrimaryPageAction(primaryAction);
    return <PageActionComponent item={internalPrimaryPageAction}/>;
}
function OnboardingPageAction({ action }: {
    action: InlineEmptyStateOnboardingSecondaryPageAction;
}) {
    const internalPageAction = toInternalPageAction(action);
    return <PageActionComponent item={internalPageAction}/>;
}
function OnboardingExternalPageAction({ externalAction }: {
    externalAction: InlineEmptyStateExternalOnboardingPageAction;
}) {
    const internalPageAction = toInternalPageAction({ type: "button", buttonType: "secondary", icon: <ArrowUpRightFromSquareIcon size={16}/>, ...externalAction });
    return <PageActionComponent item={internalPageAction}/>;
}
const inlineEmptyStateOnboardingStyles = {
    root: css({
        padding: space[48],
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    }),
    heading: css({
        color: themeTokens.color.text.primary,
        font: text.heading.small,
        margin: 0,
        marginTop: space[16],
    }),
    actions: css({
        marginTop: space[24],
        display: "flex",
        gap: space[16],
        alignItems: "center",
        "@media (max-width: 1088px)": {
            justifyContent: "center",
            flexWrap: "wrap",
        },
    }),
    description: css({
        color: themeTokens.color.text.secondary,
        font: text.body.regular.medium,
        margin: 0,
        marginTop: space[8],
    }),
};
